import { ColumnDefWithFilters, ColumnGroup } from '../types'
import { gearStats, statPercentageToColor, useSimpleColumns } from '../utils'
import {
  itemEquipmentSlots,
  itemSlotLabels,
} from 'app/frontend/types/custom/ItemSlot'
import { useMemo } from 'react'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { wowheadUrl } from 'app/frontend/base/helpers'
import { itemQualityClasses } from 'app/frontend/types/custom/ItemQuality'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import Select from 'app/frontend/base/components/Select'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import {
  colorByTrack,
  upgradeTracks,
} from 'app/frontend/types/custom/UpgradeTrack'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

const tierSlots = ['head', 'shoulder', 'chest', 'hands', 'legs'] as const

export const ilvlDisplayOptions = [
  { label: 'Highest ever', value: 'best' },
  { label: 'Current', value: 'current' },
]

export const slotDisplayOptions = [
  { label: 'Name', value: 'name' },
  { label: 'Item level', value: 'ilvl' },
  { label: 'Upgrade track', value: 'track' },
]

export const useGearOverviewColumns = (): ColumnDefWithFilters => {
  const { value: slotDisplay, setValue: setSlotDisplay } = useAuditFilterState<
    'name' | 'ilvl' | 'track'
  >(
    'slotDisplay',
    'ilvl',
    slotDisplayOptions.map((o) => o.value),
  )
  const { value: totalIlvlDisplay, setValue: setTotalIlvlDisplay } =
    useAuditFilterState<'best' | 'current'>(
      'totalIlvlDisplay',
      'best',
      ilvlDisplayOptions.map((o) => o.value),
    )

  const {
    data: { gameVersion },
  } = usePageInfo()

  const collectionGroups: ColumnGroup[] = useMemo(
    () => [
      {
        id: 'total_ilvl',
        header: '',
        subColumns: [
          {
            id: 'total_ilvl',
            header: 'Item level',
            field:
              totalIlvlDisplay === 'best'
                ? 'highest_ilvl_ever_equipped'
                : 'ilvl',
            cellWrapper: (value) => {
              return <span>{value ? Number(value).toFixed(2) : ''}</span>
            },
          },
          {
            id: 'sparks_used',
            header: 'Sparks used',
            field: 'ingenuity_sparks_equipped',
            cellWrapper: (value, member) => {
              const sparkSlots = itemEquipmentSlots
                .filter((slot) => member.data?.[`spark_${slot}_id`])
                .map((slot) => itemSlotLabels[slot])

              return (
                <Tooltip>
                  <TooltipTrigger>
                    {Math.max(Number(value), sparkSlots.length)}
                  </TooltipTrigger>
                  {sparkSlots.length > 0 ? (
                    <TooltipContent>{sparkSlots.join(', ')}</TooltipContent>
                  ) : null}
                </Tooltip>
              )
            },
          },
        ],
      },
      {
        id: 'tier',
        header: 'Tier items obtained',
        rankFields: tierSlots.map((slot) => `tier_${slot}_ilvl`),
        subColumns: [
          {
            id: 'set',
            header: 'Set',
            cellWrapper: (_, member) => {
              const slotsObtained = tierSlots.filter(
                (slot) => Number(member.data?.[`tier_${slot}_ilvl`]) > 0,
              ).length

              return (
                <span
                  className={cn(
                    'whitespace-nowrap',
                    slotsObtained >= 4
                      ? 'text-success-foreground'
                      : slotsObtained >= 2
                      ? 'text-warning-foreground'
                      : 'text-destructive-foreground',
                  )}
                >
                  {slotsObtained} / {tierSlots.length}
                </span>
              )
            },
          },
          ...tierSlots.map((slot) => ({
            id: `tier_${slot}`,
            header: () => {
              const label = itemSlotLabels[slot]
              return (
                <Tooltip>
                  <TooltipTrigger className='uppercase'>
                    {label[0]}
                  </TooltipTrigger>
                  <TooltipContent>{label}</TooltipContent>
                </Tooltip>
              )
            },
            field: `tier_${slot}_difficulty`,
            cellWrapper: (value: string | number) => {
              const fullTrack = upgradeTracks.find(
                (track) => track[0] === value,
              )
              return (
                <span className={cn(fullTrack ? colorByTrack[fullTrack] : '')}>
                  {value}
                </span>
              )
            },
          })),
        ],
      },
      {
        id: 'stat_distribution',
        header: 'Gear stat distribution (%)',
        rankFields: null,
        subColumns: gearStats.map((stat) => ({
          id: stat.value,
          header: () => (
            <Tooltip>
              <TooltipTrigger>{stat.label}</TooltipTrigger>
              <TooltipContent>{stat.fullLabel}</TooltipContent>
            </Tooltip>
          ),
          field: `${stat.value}_gear_percentage`,
          cellWrapper: (value) => (
            <span className={cn(statPercentageToColor(Number(value)))}>
              {value}
            </span>
          ),
        })),
      },
      {
        id: 'equipped',
        header: 'Equipped gear',
        rankFields: null,
        subColumns: itemEquipmentSlots.map((slot) => ({
          id: `equipped_${slot}`,
          header: () => <span>{itemSlotLabels[slot]}</span>,
          field: slotDisplay === 'ilvl' ? `${slot}_ilvl` : `${slot}_name`,
          cellWrapper: (_, member) => {
            const item = member.gear?.[slot]

            return item ? (
              <a
                href={wowheadUrl(gameVersion, member.gear?.[slot])}
                target='_blank'
                rel='noreferrer'
                className={`whitespace-nowrap font-semibold inline-flex gap-2 items-center ${
                  slotDisplay === 'track'
                    ? colorByTrack[item.upgrade_level?.track || 'None']
                    : itemQualityClasses[item.quality || 0]
                }`}
              >
                <ItemIcon id={item.id} />
                <span>
                  {slotDisplay === 'name'
                    ? item.name
                    : slotDisplay === 'ilvl'
                    ? item.ilvl
                    : item.upgrade_level?.track}
                </span>
              </a>
            ) : null
          },
        })),
      },
    ],
    [gameVersion, slotDisplay, totalIlvlDisplay],
  )

  const columns = useSimpleColumns(collectionGroups, [
    slotDisplay,
    totalIlvlDisplay,
  ])

  return {
    columns,
    filters: [
      <AuditFilter label='Shown item level' key='totalIlvlDisplay'>
        <Select
          className='w-40'
          value={totalIlvlDisplay}
          setValue={(value) => setTotalIlvlDisplay(value as 'best' | 'current')}
          options={ilvlDisplayOptions}
        />
      </AuditFilter>,
      <AuditFilter label='Gear display' key='slotDisplay'>
        <Select
          className='w-40'
          value={slotDisplay}
          setValue={(value) =>
            setSlotDisplay(value as 'name' | 'ilvl' | 'track')
          }
          options={slotDisplayOptions}
        />
      </AuditFilter>,
    ],
    initialSortBy: [{ id: 'total_ilvl', desc: true }],
  }
}
